import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowUpRight, Building2, MapPin } from "lucide-react";

import { usePublicCities } from "@/hooks/use-public-cities";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { AutoT } from "@/i18n/AutoT";

/** Business mode operates in Helsinki only — other cities stay hidden here. */
const BUSINESS_CITY_SLUGS = ["helsinki"];

export const Route = createFileRoute("/business/cities")({
  head: () => {
    const url = "https://whatfinland.com/business/cities";
    return {
      meta: [
        { title: "Business Cities In Finland — Helsinki | what Finland" },
        {
          name: "description",
          content:
            "Where what Finland business mode operates today: Helsinki — coworking, business stays, corporate events and afterwork picks.",
        },
        { property: "og:title", content: "Business Cities In Finland — Helsinki" },
        {
          property: "og:description",
          content: "Business mode city coverage: Helsinki coworking, stays, events and dining.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BusinessCitiesPage,
});

function BusinessCitiesPage() {
  const cities = usePublicCities();
  const places = usePublicPlaces();

  const shown = useMemo(
    () => cities.filter((c) => BUSINESS_CITY_SLUGS.includes(c.slug)),
    [cities],
  );

  const countFor = (slug: string, name: string) =>
    places.filter((p) => {
      const key = String(p.city || "").toLowerCase().trim();
      return key === slug.toLowerCase() || key === name.toLowerCase();
    }).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 md:pt-10 pb-20">
      <header className="mb-8 md:mb-10">
        <div className="flex items-center gap-3.5 sm:gap-4">
          <span className="grid size-12 sm:size-14 shrink-0 place-items-center rounded-2xl bg-navy text-white shadow-[0_12px_28px_-12px_oklch(0.22_0.09_260/0.75)]">
            <Building2 className="size-6" />
          </span>
          <div className="min-w-0">
            <p className="font-body text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/45">
              <AutoT text="Business coverage" />
            </p>
            <h1
              style={{ textTransform: "none" }}
              className="mt-0.5 text-[2.25rem] sm:text-[3rem] font-display font-extrabold leading-[0.9] tracking-tighter text-navy"
            >
              <AutoT text="Cities" />
            </h1>
          </div>
        </div>
        <p className="mt-4 max-w-xl font-body text-[13px] sm:text-[15px] text-navy/60 leading-relaxed">
          <AutoT text="Business mode runs in Helsinki right now — coworking, business stays, corporate events and afterwork dining. More cities are coming." />
        </p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {shown.map((c) => (
          <Link
            key={c.slug}
            to="/business"
            className="group relative overflow-hidden rounded-[1.5rem] aspect-[4/3] ring-1 ring-black/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_44px_-22px_oklch(0.22_0.09_260/0.45)]"
          >
            {c.image_url ? (
              <img
                src={c.image_url}
                alt={`Business travel in ${c.name}, Finland — coworking, stays and events`}
                width={800}
                height={600}
                loading="eager"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.06]"
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(140deg, var(--navy-deep), var(--navy) 50%, ${c.accent || "var(--royal)"})`,
                }}
              />
            )}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, oklch(0.22 0.14 270 / 0.9) 0%, oklch(0.22 0.14 270 / 0.45) 40%, transparent 78%)",
              }}
            />
            <div className="absolute inset-x-0 bottom-0 p-4 text-white">
              <h2
                style={{ textTransform: "none" }}
                className="font-heading leading-[0.95] tracking-[-0.03em] text-[1.4rem]"
              >
                {c.name}
              </h2>
              {c.tagline ? (
                <p
                  style={{ textTransform: "none" }}
                  className="mt-1.5 font-body text-[13px] leading-snug text-white/85 line-clamp-2"
                >
                  {c.tagline}
                </p>
              ) : null}
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 backdrop-blur-md px-2 py-1 ring-1 ring-white/15 text-[11px] font-semibold">
                  <MapPin className="size-3 shrink-0" />
                  <span className="tabular-nums">{countFor(c.slug, c.name)}</span>
                  <span className="font-body font-medium text-white/80">
                    <AutoT text="places" />
                  </span>
                </span>
                <span className="inline-flex items-center gap-0.5 text-[12px] font-semibold tracking-tight">
                  <AutoT text="Open business" />
                  <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </div>
          </Link>
        ))}

        {shown.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            <AutoT text="No business cities available yet." />
          </p>
        ) : null}
      </section>
    </div>
  );
}
